import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '../../contexts/TranslationContext';

interface FooterProps {
  locale?: string;
}

const FOOTER_HEIGHT = 560; // in px, adjust as needed

const Footer: React.FC<FooterProps> = ({ locale: propLocale }) => {
  const navigate = useNavigate();
  const {
    translations: t,
    language,
    changeLanguage,
    isLoading,
    getUrlLanguagePrefix,
  } = useTranslations();

  useEffect(() => {
    if (propLocale && propLocale !== language) {
      changeLanguage(propLocale);
    }
  }, [propLocale, language, changeLanguage]);

  const handleLinkClick = (path: string) => {
    const languagePrefix = getUrlLanguagePrefix(language);
    const fullPath = `${languagePrefix}${path}`;
    navigate(fullPath);
  };

  // Loading skeleton with fixed height to prevent layout shift
  if (isLoading || !t || !t.navigations) {
    return (
      <footer
        className="bg-black text-white mt-1 flex items-center justify-center"
        style={{ height: FOOTER_HEIGHT }}
        aria-busy="true"
        aria-label="Loading footer"
      >
        <div className="animate-pulse w-full max-w-7xl px-4 py-8">
          <div className="h-6 bg-gray-700 rounded mb-4 w-32"></div>
          <div className="grid grid-cols-4 gap-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-3">
                <div className="h-4 bg-gray-700 rounded w-24"></div>
                <div className="h-3 bg-gray-600 rounded w-full max-w-xs"></div>
                <div className="h-3 bg-gray-600 rounded w-full max-w-xs"></div>
              </div>
            ))}
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer
      className="bg-black text-white mt-1 flex flex-col items-center justify-center"
      style={{ height: FOOTER_HEIGHT }}
    >
      <div className="max-w-7xl mx-auto px-4 py-8 w-full">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4 max-w-xs truncate">
            <h3 className="text-xl font-bold truncate">Global24</h3>
            <button
              onClick={() => handleLinkClick('/archive')}
              className="block text-gray-400 hover:text-white transition-colors truncate"
              title={t?.navigations?.footer?.archive}
            >
              {t?.navigations?.footer?.archive || 'News Archive'}
            </button>
            <p className="text-gray-400 truncate" title={t?.navigations?.footer?.copyright}>
              {t?.navigations?.footer?.copyright || '© 2024 Global24. All rights reserved.'}
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4 max-w-xs">
            <h3 className="text-lg font-semibold truncate">
              {t?.navigations?.footer?.about || 'About Us'}
            </h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => handleLinkClick('/about')}
                  className="text-gray-400 hover:text-white transition-colors truncate"
                  title={t?.navigations?.footer?.about}
                >
                  {t?.navigations?.footer?.about || 'About Us'}
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleLinkClick('/contact')}
                  className="text-gray-400 hover:text-white transition-colors truncate"
                  title={t?.navigations?.footer?.contact}
                >
                  {t?.navigations?.footer?.contact || 'Contact'}
                </button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="space-y-4 max-w-xs">
            <h3 className="text-lg font-semibold truncate">
              {t?.navigations?.footer?.terms || 'Terms of Service'}
            </h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => handleLinkClick('/privacy')}
                  className="text-gray-400 hover:text-white transition-colors truncate"
                  title={t?.navigations?.footer?.privacy}
                >
                  {t?.navigations?.footer?.privacy || 'Privacy Policy'}
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleLinkClick('/terms')}
                  className="text-gray-400 hover:text-white transition-colors truncate"
                  title={t?.navigations?.footer?.terms}
                >
                  {t?.navigations?.footer?.terms || 'Terms of Service'}
                </button>
              </li>
            </ul>
          </div>

          {/* Social & Subscribe */}
          <div className="space-y-4 max-w-xs">
            <h3 className="text-lg font-semibold truncate">
              {t?.navigations?.footer?.followUs || 'Follow Us'}
            </h3>
            <div className="flex space-x-4">
              {/* Your social icons here */}
            </div>
            <div className="mt-4">
              <h4 className="text-lg font-semibold mb-2 truncate">
                {t?.navigations?.footer?.subscribe || 'Subscribe to Newsletter'}
              </h4>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Email"
                  className="bg-gray-800 text-white px-4 py-2 rounded-l focus:outline-none flex-grow"
                />
                <button className="bg-red-600 px-4 py-2 rounded-r hover:bg-red-700 transition-colors">
                  →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
